import * as THREE from 'three'
import type { AudioFrame } from '../types'

/**
 * Automatic cinematic camera that reacts to the music.
 *
 * Orbits the center with gentle drift, chains "shots" (new angles/zoom) every so
 * often or on strong beats, pushes in when the energy rises, and adds a subtle
 * shake on every beat.
 */
export class CameraDirector {
  private radius = 7
  private theta = 0 // azimuth
  private phi = Math.PI / 2 // polar (from +Y)
  private tRadius = 7
  private tTheta = 0
  private tPhi = Math.PI / 2
  private shotTimer = 0
  private shotDuration = 7
  private rollTarget = 0
  private roll = 0
  private shake = new THREE.Vector3()
  private pos = new THREE.Vector3()
  private look = new THREE.Vector3(0, 0, 0)

  private newShot(energetic: boolean) {
    this.tTheta = this.theta + (Math.random() * 2 - 1) * (energetic ? 1.8 : 1.0)
    this.tPhi = THREE.MathUtils.clamp(
      Math.PI / 2 + (Math.random() * 2 - 1) * 0.55,
      0.45,
      Math.PI - 0.45,
    )
    this.tRadius = 5 + Math.random() * (energetic ? 3 : 5)
    this.rollTarget = (Math.random() * 2 - 1) * (energetic ? 0.25 : 0.1)
    this.shotTimer = 0
    this.shotDuration = 5 + Math.random() * 5
  }

  update(camera: THREE.PerspectiveCamera, frame: AudioFrame, dt: number) {
    // Base drift + shot timer.
    this.tTheta += dt * 0.06
    this.shotTimer += dt
    if (
      this.shotTimer > this.shotDuration ||
      (frame.beat && frame.beatIntensity > 0.9 && this.shotTimer > 2.5)
    ) {
      this.newShot(frame.level > 0.4)
    }

    // Framerate-independent smoothing.
    const k = 1 - Math.pow(0.0015, dt)

    // Push-in as the energy rises.
    const wantRadius = THREE.MathUtils.clamp(this.tRadius - frame.level * 2.6, 3, 11)
    this.radius += (wantRadius - this.radius) * k
    this.theta += (this.tTheta - this.theta) * k
    this.phi += (this.tPhi - this.phi) * k
    this.roll += (this.rollTarget - this.roll) * k * 0.5

    // Shake on beats.
    const s = frame.beatIntensity * 0.18
    this.shake.set(
      (Math.random() * 2 - 1) * s,
      (Math.random() * 2 - 1) * s,
      (Math.random() * 2 - 1) * s,
    )

    const sinPhi = Math.sin(this.phi)
    this.pos.set(
      this.radius * sinPhi * Math.sin(this.theta),
      this.radius * Math.cos(this.phi),
      this.radius * sinPhi * Math.cos(this.theta),
    )
    camera.position.copy(this.pos).add(this.shake)
    camera.up.set(Math.sin(this.roll), Math.cos(this.roll), 0)
    camera.lookAt(this.look)
  }

  /** Re-syncs the internal state with the current camera (when the mode is activated). */
  syncFrom(camera: THREE.PerspectiveCamera) {
    this.radius = camera.position.length() || 7
    this.tRadius = this.radius
    this.theta = Math.atan2(camera.position.x, camera.position.z)
    this.tTheta = this.theta
    this.phi = Math.acos(THREE.MathUtils.clamp(camera.position.y / this.radius, -1, 1))
    this.tPhi = this.phi
    this.roll = 0
    this.rollTarget = 0
    this.shotTimer = 0
  }
}
